import React, { useState, useEffect, useMemo } from 'react';
import '../styles/CompanyProfile.css';
import fetchApi from '../utils/api.js';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import PageSkeleton from '../components/ui/PageSkeleton';

// Dashboard Components
import DashboardCharts from '../components/DashboardCharts';
import DashboardTables from '../components/DashboardTables';
import LowStockAlertTable from '../components/LowStockAlertTable';
import PieChartComponent from '../components/PieChartComponent';

// Sub-views for inline rendering inside Company Detail tab
import PurchaseInvoiceList from './PurchaseInvoiceList';
import InvoiceList from './InvoiceList';
import SellerList from './SellerList';
import CustomerList from './CustomerList';
import TransporterList from './TransporterList';
import ProductList from './ProductList';
import CategoryList from './CategoryList';
import InStock from './InStock';
import OutStock from './OutStock';
import CustomerProfile from './CustomerProfile';
import SellerProfile from './SellerProfile';
import TransporterProfile from './TransporterProfile';
import PlanPaymentModal from '../components/modals/PlanPaymentModal';
import { usePermissionContext, PermissionContext } from '../contexts/PermissionContext.js';

const ICONS = {
    purchase: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
            <polyline points="14 2 14 8 20 8"></polyline>
            <line x1="16" y1="13" x2="8" y2="13"></line>
            <line x1="16" y1="17" x2="8" y2="17"></line>
            <polyline points="10 9 9 9 8 9"></polyline>
        </svg>
    ),
    invoice: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
            <polyline points="14 2 14 8 20 8"></polyline>
            <line x1="16" y1="13" x2="8" y2="13"></line>
            <line x1="16" y1="17" x2="8" y2="17"></line>
            <line x1="10" y1="9" x2="8" y2="9"></line>
        </svg>
    ),
    seller: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 21h18"></path>
            <path d="M3 7v1a3 3 0 0 0 6 0V7m0 1a3 3 0 0 0 6 0V7m0 1a3 3 0 0 0 6 0V7H3l2-4h14l2 4"></path>
            <path d="M5 21V10.85"></path>
            <path d="M19 21V10.85"></path>
            <path d="M9 21v-4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v4"></path>
        </svg>
    ),
    customer: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
            <circle cx="9" cy="7" r="4"></circle>
            <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
            <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
        </svg>
    ),
    transporter: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="1" y="3" width="15" height="13"></rect>
            <polygon points="16 8 20 8 23 11 23 16 16 16 16 8"></polygon>
            <circle cx="5.5" cy="18.5" r="2.5"></circle>
            <circle cx="18.5" cy="18.5" r="2.5"></circle>
        </svg>
    ),
    product: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
            <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
            <line x1="12" y1="22.08" x2="12" y2="12"></line>
        </svg>
    ),
    category: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path>
            <line x1="7" y1="7" x2="7.01" y2="7"></line>
        </svg>
    ),
    inStock: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 8l-2-2H5L3 8v10a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V8z"></path>
            <path d="M10 12l2 2 2-2"></path>
            <path d="M12 14V7"></path>
        </svg>
    ),
    outStock: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 8l-2-2H5L3 8v10a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V8z"></path>
            <path d="M14 12l-2-2-2 2"></path>
            <path d="M12 10v7"></path>
        </svg>
    ),
    money: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M6 4h12"></path>
            <path d="M6 9h12"></path>
            <path d="M17 9c0 7-11 7-11 7"></path>
            <path d="M14.5 21L6 13"></path>
        </svg>
    ),
    alert: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
        </svg>
    ),
};

const CompanyProfile = ({ 
    company, 
    onBack, 
    onPermissions, 
    onChat, 
    onEditUser, 
    onDeleteUser,
    onAddUser,
    onPreviewInvoice,
    onPreviewPurchaseInvoice,
    onAddInvoice,
    onEditInvoice,
    onAddPurchaseInvoice,
    onEditPurchaseInvoice,
    onNavigateToCustomerProfile,
    onNavigateToSellerProfile,
    onNavigateToTransporterProfile
}) => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState(() => {
        const params = new URLSearchParams(window.location.search);
        return params.get('tab') || 'users';
    });
    const [activeSubView, setActiveSubView] = useState('dashboard'); // 'dashboard' or list name
    const [inlineProfileType, setInlineProfileType] = useState(null); // 'Customer' | 'Seller' | 'Transporter' | null
    const [inlineProfileData, setInlineProfileData] = useState(null);
    const [previousSubView, setPreviousSubView] = useState(null);
    const [dashboardLoading, setDashboardLoading] = useState(false);
    const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
    const [dashboardStats, setDashboardStats] = useState({
        counts: {},
        lowStockProducts: [],
        recentPurchasePayments: [],
        recentSalesPayments: [],
        pieData: [],
        salesHistory: [],
        purchaseVsPayment: []
    });
    
    const originalContext = usePermissionContext();
    const currentUser = JSON.parse(sessionStorage.getItem('user'));
    const currentUserRole = currentUser?.role?.toUpperCase();
    const currentUserId = currentUser?._id || currentUser?.id;
    const isPrivileged = currentUserRole === 'SUPER_ADMIN';

    const canShowAll = originalContext.hasPermission('Company', 'show_all') || originalContext.hasPermission('Company', 'showAll');

    const wrappedContext = useMemo(() => {
        if (!canShowAll && currentUserRole !== 'SUPER_ADMIN') return originalContext;
        return {
            ...originalContext,
            hasPermission: (moduleName, action) => {
                // 'show_all' grants view access to all modules within the company profile context
                if (action === 'view') return true;
                return originalContext.hasPermission(moduleName, action);
            }
        };
    }, [originalContext, canShowAll, currentUserRole]);

    const hasPermission = wrappedContext.hasPermission;

    // If the user has 'show_all' for companies, they should see the profile data like a super admin
    const canViewCompany = isPrivileged || canShowAll || hasPermission('Company', 'view');
    const canViewUser = isPrivileged || canShowAll || hasPermission('User', 'view');
    const canAddUser = isPrivileged || hasPermission('User', 'add');
    const canUpdateUser = isPrivileged || hasPermission('User', 'update');
    const canDeleteUser = isPrivileged || hasPermission('User', 'delete');
    const canViewPlan = isPrivileged || canShowAll || hasPermission('Plan', 'view');
    const canPayPlan = isPrivileged || hasPermission('Plan Payment', 'add');
    const canUpdateCompany = isPrivileged || hasPermission('Company', 'update');

    const currentUserCompanyId = currentUser?.companyId || currentUser?.company_id;
    const isOwnCompany = currentUserCompanyId && company && (String(currentUserCompanyId) === String(company._id || company.id));
    const hasAccessToThisCompany = isPrivileged || isOwnCompany || canShowAll;

    useEffect(() => {
        if (!hasAccessToThisCompany) return;
        if (!canViewUser) {
            if (canViewPlan) {
                setActiveTab('plan');
            } else if (canViewCompany) {
                setActiveTab('dashboard');
            }
        }
    }, [canViewUser, canViewPlan, canViewCompany, hasAccessToThisCompany]);

    useEffect(() => {
        if (activeSubView === 'dashboard') {
            setInlineProfileType(null);
            setInlineProfileData(null);
            setPreviousSubView(null);
        }
    }, [activeSubView]);

    const handleInlineNavigateToCustomer = (customer) => {
        setPreviousSubView(activeSubView);
        setInlineProfileType('Customer');
        setInlineProfileData(customer);
        setActiveSubView('Customer');
    };

    const handleInlineNavigateToSeller = (seller) => {
        setPreviousSubView(activeSubView);
        setInlineProfileType('Seller');
        setInlineProfileData(seller);
        setActiveSubView('Seller');
    };

    const handleInlineNavigateToTransporter = (transporter) => {
        setPreviousSubView(activeSubView);
        setInlineProfileType('Transporter');
        setInlineProfileData(transporter);
        setActiveSubView('Transporter');
    };

    useEffect(() => {
        if (company) {
            const companyId = company._id || company.id;
            const currentParams = new URLSearchParams(window.location.search);
            if (companyId && currentParams.get('companyId') !== companyId) {
                currentParams.set('companyId', companyId);
                const newRelativePathQuery = window.location.pathname + '?' + currentParams.toString();
                window.history.replaceState(null, '', newRelativePathQuery);
            }
        }
    }, [company]);

    useEffect(() => {
        if (company && hasAccessToThisCompany && canViewUser) {
            fetchCompanyUsers();
        }
    }, [company, hasAccessToThisCompany, canViewUser]);

    useEffect(() => {
        if (company && activeTab === 'dashboard' && canViewCompany && hasAccessToThisCompany) {
            fetchCompanyDashboardStats();
        }
    }, [company, activeTab, canViewCompany, hasAccessToThisCompany]);

    const fetchCompanyUsers = async () => {
        try {
            setLoading(true);
            const companyId = company._id || company.id;
            const data = await fetchApi(`/users?companyId=${companyId}`);

            if (data.users && Array.isArray(data.users)) {
                setUsers(data.users);
            } else if (Array.isArray(data)) {
                setUsers(data);
            }
        } catch (error) {
            console.error("Error fetching company users:", error);
            toast.error("Failed to fetch users");
        } finally {
            setLoading(false);
        }
    };

    const fetchCompanyDashboardStats = async () => {
        try {
            setDashboardLoading(true);
            const companyId = company._id || company.id;
            const data = await fetchApi(`/dashboard/stats?companyId=${companyId}`);
            if (data) {
                setDashboardStats(data);
            }
        } catch (error) {
            console.error("Error fetching company dashboard stats:", error);
            toast.error("Failed to fetch dashboard stats");
        } finally {
            setDashboardLoading(false);
        }
    };

    const [paymentHistory, setPaymentHistory] = useState([]);
    const [paymentHistoryLoading, setPaymentHistoryLoading] = useState(false);

    const fetchPaymentHistory = async () => {
        try {
            setPaymentHistoryLoading(true);
            const companyId = company._id || company.id;
            const data = await fetchApi(`/plan-payment/company/${companyId}`);
            setPaymentHistory(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error("Error fetching payment history:", error);
        } finally {
            setPaymentHistoryLoading(false);
        }
    };

    useEffect(() => {
        if (company && activeTab === 'plan' && hasAccessToThisCompany && canViewPlan) {
            fetchPaymentHistory();
        }
    }, [company, activeTab, hasAccessToThisCompany, canViewPlan]);

    const mappedRecentPurchasePayments = useMemo(() => {
        if (!dashboardStats.recentPurchasePayments) return [];
        return dashboardStats.recentPurchasePayments.map(p => ({
            id: p._id,
            partyName: p.buyerTradeName || 'N/A',
            reference: p.paymentModeName || `PAY-${p.purchaseOrderPaymentId}`,
            date: p.paymentDate,
            amount: p.paymentAmount
        }));
    }, [dashboardStats.recentPurchasePayments]);

    const mappedRecentSalesPayments = useMemo(() => {
        if (!dashboardStats.recentSalesPayments) return [];
        return dashboardStats.recentSalesPayments.map(p => ({
            id: p._id,
            partyName: p.customerName || 'N/A',
            reference: p.paymentModeName || `PAY-${p.invoicePaymentId}`,
            date: p.paymentDate,
            amount: p.paymentAmount
        }));
    }, [dashboardStats.recentSalesPayments]);

    const mappedLowStock = useMemo(() => {
        if (!dashboardStats.lowStockProducts) return [];
        return dashboardStats.lowStockProducts.map(p => ({
            id: p.id || p._id,
            name: p.product_name || p.productName,
            sku: p.product_hsn_code || p.productHsnCode || 'N/A',
            category: p.Category?.category_name || p.Category?.categoryName || p.category_name || 'N/A',
            stock: p.product_stock !== undefined ? p.product_stock : (p.productStock !== undefined ? p.productStock : 0)
        }));
    }, [dashboardStats.lowStockProducts]);

    const cards = useMemo(() => {
        const counts = dashboardStats.counts || {};
        const allCards = [
            { title: 'Purchase Invoice', count: counts.purchaseInvoices || 0, icon: ICONS.purchase, color: '#7c3aed', module: 'Purchase Invoice' },
            { title: 'Invoice', count: counts.invoices || 0, icon: ICONS.invoice, color: '#0891b2', module: 'Sales Invoice' },
            { title: 'Seller', count: counts.sellers || 0, icon: ICONS.seller, color: '#111827', module: 'Seller' },
            { title: 'Customer', count: counts.customers || 0, icon: ICONS.customer, color: '#2563eb', module: 'Customer' },
            { title: 'Transporter', count: counts.transporters || 0, icon: ICONS.transporter, color: '#4f46e5', module: 'Transporter' },
            { title: 'Product', count: counts.products || 0, icon: ICONS.product, color: '#374151', module: 'Product' },
            { title: 'Category', count: counts.categories || 0, icon: ICONS.category, color: '#dc2626', module: 'Category' },
            { title: 'Stock In', count: counts.totalInStock || 0, icon: ICONS.inStock, color: '#16a34a', module: 'InStock' },
            { title: 'Stock Out', count: counts.totalOutStock || 0, icon: ICONS.outStock, color: '#16a34a', module: 'OutStock' },
            { title: 'Purchase Amount', count: `₹${(counts.totalPurchaseAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, icon: ICONS.money, color: '#0b3a54', module: 'Purchase Invoice' },
            { title: 'Sale Amount', count: `₹${(counts.totalSalesAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, icon: ICONS.money, color: '#059669', module: 'Sales Invoice' }
        ];

        return allCards.filter(card => {
            if (isPrivileged || canShowAll) return true;
            if (!card.module) return true;
            return hasPermission(card.module, 'view');
        });
    }, [dashboardStats.counts, isPrivileged, canShowAll, hasPermission]);

    const handleStatusChange = async (user, newStatus) => {
        try {
            await fetchApi(`/users/${user._id || user.id}`, {
                method: 'PUT',
                body: JSON.stringify({ userStatus: parseInt(newStatus) })
            });
            toast.success('Status updated successfully');
            fetchCompanyUsers(); // Refresh the list
        } catch (error) {
            console.error('Error updating status:', error);
            toast.error('Failed to update status');
        }
    };

    const handleDeleteUser = (id) => {
        Swal.fire({
            title: 'Are you sure?',
            text: 'Do you want to delete this user?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!',
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await fetchApi(`/users/${id}`, { method: 'DELETE' });
                    Swal.fire('Deleted!', 'User has been deleted.', 'success');
                    fetchCompanyUsers(); // Refresh the list
                } catch (error) {
                    console.error(error);
                }
            }
        });
    };

    const formatDate = (date) => {
        if (!date) return '—';
        return new Date(date).toLocaleDateString('en-IN', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    if (!company) return <div className="company-profile-container">No Company Selected</div>;

    if (!hasAccessToThisCompany) {
        return (
            <div className="company-profile-container">
                <div className="company-profile-header">
                    <div className="header-left">
                        <h2 className="company-profile-title">{company.companyName}</h2>
                        <span className="company-profile-subtitle">{company.companyEmail}</span>
                    </div>
                    {onBack && (
                        <button className="company-profile-back-btn" onClick={onBack}>
                            ← Back to List
                        </button>
                    )}
                </div>
                <div style={{ padding: '40px', textAlign: 'center', background: '#fff', borderRadius: '12px', marginTop: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ margin: '0 auto 16px' }}>
                        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                        <line x1="12" y1="9" x2="12" y2="13"></line>
                        <line x1="12" y1="17" x2="12.01" y2="17"></line>
                    </svg>
                    <h3 style={{ fontSize: '20px', color: '#1e293b', marginBottom: '8px', fontWeight: '700' }}>Permission Denied</h3>
                    <p style={{ color: '#64748b', fontSize: '15px' }}>You do not have permission to view data for this company.</p>
                </div>
            </div>
        );
    }

    return (
        <PermissionContext.Provider value={wrappedContext}>
            <div className="company-profile-container">
            <div className="company-profile-header">
                <div className="header-left">
                    <h2 className="company-profile-title">{company.companyName}</h2>
                    <span className="company-profile-subtitle">{company.companyEmail}</span>
                </div>
                {onBack && (
                    <button className="company-profile-back-btn" onClick={onBack}>
                        ← Back to List
                    </button>
                )}
            </div>

            <div className="company-profile-tabs-wrapper">
                <div className="company-profile-tabs">
                    {canViewUser && (
                        <button
                            className={`profile-tab ${activeTab === 'users' ? 'active' : ''}`}
                            onClick={() => { setActiveTab('users'); setActiveSubView('dashboard'); }}
                        >
                            Users List
                        </button>
                    )}
                    {canViewPlan && (
                        <button
                            className={`profile-tab ${activeTab === 'plan' ? 'active' : ''}`}
                            onClick={() => { setActiveTab('plan'); setActiveSubView('dashboard'); }}
                        >
                            Plan Details
                        </button>
                    )}
                    {canViewCompany && (
                        <button
                            className={`profile-tab ${activeTab === 'dashboard' ? 'active' : ''}`}
                            onClick={() => { setActiveTab('dashboard'); setActiveSubView('dashboard'); }}
                        >
                            Company Detail
                        </button>
                    )}
                </div>

                <div className="company-profile-tab-content">
                    {activeTab === 'users' && canViewUser ? (
                        <div className="profile-panel users-panel">
                            <div className="panel-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                    <div className="panel-title">Users of this Company</div>
                                    <span className="user-count">{users.length} Users</span>
                                </div>
                                {canAddUser && onAddUser && (
                                    <button 
                                        className="profile-tab-action-btn"
                                        onClick={() => onAddUser(company)}
                                        style={{
                                            padding: '8px 16px',
                                            backgroundColor: '#0b3a54',
                                            color: '#fff',
                                            border: 'none',
                                            borderRadius: '6px',
                                            cursor: 'pointer',
                                            fontWeight: '600',
                                            fontSize: '13px',
                                            transition: 'all 0.2s ease',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '6px'
                                        }}
                                        onMouseOver={(e) => e.target.style.opacity = '0.9'}
                                        onMouseOut={(e) => e.target.style.opacity = '1'}
                                    >
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                            <line x1="12" y1="5" x2="12" y2="19"></line>
                                            <line x1="5" y1="12" x2="19" y2="12"></line>
                                        </svg>
                                        ADD USER
                                    </button>
                                )}
                            </div>
                            <div className="panel-body">
                                {loading ? (
                                    <PageSkeleton />
                                ) : users.length > 0 ? (
                                    <div className="table-wrapper">
                                        <table className="panel-table">
                                            <thead>
                                                <tr>
                                                    <th>NAME</th>
                                                    <th>MOBILE</th>
                                                    <th>EMAIL</th>
                                                    <th>ROLE</th>
                                                    {canUpdateUser && <th>PERMISSION</th>}
                                                    <th>STATUS</th>
                                                    <th>ACTION</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {users.map(user => (
                                                    <tr key={user._id}>
                                                        <td>
                                                            <div className="user-cell">
                                                                {user.userProfile ? (
                                                                    <img src={user.userProfile} alt="" className="user-avatar" />
                                                                ) : (
                                                                    <div className="user-avatar-placeholder">
                                                                        {user.userName?.charAt(0)}
                                                                    </div>
                                                                )}
                                                                <span>{user.userName}</span>
                                                            </div>
                                                        </td>
                                                        <td>{user.userMobileNumber}</td>
                                                        <td>{user.userEmail}</td>
                                                        <td>
                                                            <span className={`role-badge ${user.role?.toLowerCase().replace(' ', '_')}`}>
                                                                {user.role}
                                                            </span>
                                                        </td>
                                                        {canUpdateUser && (
                                                            <td>
                                                                <span
                                                                    onClick={() => {
                                                                        onPermissions && onPermissions(user);
                                                                    }}
                                                                    style={{ color: '#2563eb', cursor: 'pointer', textDecoration: 'underline', fontWeight: '600' }}
                                                                >
                                                                    Permissions
                                                                </span>
                                                            </td>
                                                        )}
                                                        <td>
                                                            {canUpdateUser ? (
                                                                <select
                                                                    value={user.userStatus}
                                                                    onChange={(e) => handleStatusChange(user, e.target.value)}
                                                                    style={{
                                                                        padding: '4px 8px',
                                                                        borderRadius: '4px',
                                                                        border: '1px solid #cbd5e1',
                                                                        backgroundColor: user.userStatus === 1 ? '#dcfce7' : '#fee2e2',
                                                                        color: user.userStatus === 1 ? '#166534' : '#991b1b',
                                                                        fontWeight: '600',
                                                                        fontSize: '12px',
                                                                        outline: 'none',
                                                                        cursor: 'pointer'
                                                                    }}
                                                                >
                                                                    <option value={1}>Active</option>
                                                                    <option value={0}>Inactive</option>
                                                                </select>
                                                            ) : (
                                                                <span className={`status-indicator ${user.userStatus === 1 ? 'active' : 'inactive'}`}>
                                                                    {user.userStatus === 1 ? 'Active' : 'Inactive'}
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td>
                                                            <div className="profile-action-icons">
                                                                {onChat && (currentUserRole === 'SUPER_ADMIN' ? user.role === 'ADMIN' : (user._id || user.id) !== currentUserId) ? (
                                                                    <button
                                                                        type="button"
                                                                        className="profile-icon-btn"
                                                                        title="Chat"
                                                                        onClick={() => onChat(user)}
                                                                    >
                                                                        <svg viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                            <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
                                                                        </svg>
                                                                    </button>
                                                                ) : (
                                                                    <div className="profile-icon-placeholder" />
                                                                )}
                                                                {canUpdateUser && (
                                                                    <button
                                                                        type="button"
                                                                        className="profile-icon-btn"
                                                                        title="Edit"
                                                                        onClick={() => onEditUser && onEditUser(user)}
                                                                    >
                                                                        <svg viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                                                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                                                                        </svg>
                                                                    </button>
                                                                )}
                                                                {canDeleteUser && (
                                                                    <button
                                                                        type="button"
                                                                        className="profile-icon-btn"
                                                                        title="Delete"
                                                                        onClick={() => handleDeleteUser(user._id)}
                                                                    >
                                                                        <svg viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                            <polyline points="3 6 5 6 21 6"></polyline>
                                                                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                                                        </svg>
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <div className="panel-empty">No users found for this company.</div>
                                )}
                            </div>
                        </div>
                    ) : activeTab === 'plan' && canViewPlan ? (
                        <div className="profile-panel plan-panel">
                            <div className="panel-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div className="panel-title">Subscription & Plan Details</div>
                                {company.planId && canPayPlan && (() => {
                                    const hasVerifiedPayment = paymentHistory.some(p => p.paymentStatus === 'Verified');
                                    const hasPendingPayment = paymentHistory.some(p => p.paymentStatus === 'Pending');

                                    let isExpired = false;
                                    if (company.planExpiryDate) {
                                        const now = new Date();
                                        const expiry = new Date(company.planExpiryDate);
                                        isExpired = now > expiry;
                                    } else {
                                        isExpired = true;
                                    }

                                    const isDisabled = hasPendingPayment || (hasVerifiedPayment && !isExpired);
                                    
                                    let buttonLabel = "Pay for Plan";
                                    let buttonTooltip = "Pay for selected plan";
                                    if (hasPendingPayment) {
                                        buttonLabel = "Payment Pending";
                                        buttonTooltip = "Payment is pending verification by admin.";
                                    } else if (hasVerifiedPayment && !isExpired) {
                                        buttonLabel = "Payment Verified";
                                        buttonTooltip = "Payment is already verified for your active plan.";
                                    } else if (isExpired) {
                                        buttonLabel = "Renew Plan";
                                        buttonTooltip = "Plan has expired. Click to pay & renew your subscription.";
                                    }
                                    
                                    return (
                                        <button 
                                            onClick={() => {
                                                if (!isDisabled) setIsPaymentModalOpen(true);
                                            }}
                                            disabled={isDisabled}
                                            style={{
                                                padding: '8px 16px',
                                                backgroundColor: isDisabled ? '#94a3b8' : '#10b981',
                                                color: '#fff',
                                                border: 'none',
                                                borderRadius: '6px',
                                                cursor: isDisabled ? 'not-allowed' : 'pointer',
                                                fontWeight: '600',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                                opacity: isDisabled ? 0.7 : 1
                                            }}
                                            title={buttonTooltip}
                                        >
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <rect x="2" y="5" width="20" height="14" rx="2"></rect>
                                                <line x1="2" y1="10" x2="22" y2="10"></line>
                                            </svg>
                                            {buttonLabel}
                                        </button>
                                    );
                                })()}
                            </div>
                            <div className="panel-body">
                                <div className="plan-summary-grid">
                                    <div className="plan-summary-card">
                                        <div className="plan-icon">💎</div>
                                        <div className="plan-info">
                                            <div className="plan-name">{company.planName || 'No Active Plan'}</div>
                                            <div className="plan-duration">{company.planDurationDays || 0} Days Plan</div>
                                        </div>
                                    </div>

                                    <div className="details-list-card">
                                        <div className="detail-item">
                                            <span className="detail-label">Plan Price</span>
                                            <span className="detail-value">₹{company.planPrice || 0}</span>
                                        </div>
                                        <div className="detail-item">
                                            <span className="detail-label">Discount</span>
                                            <span className="detail-value" style={{ color: '#1e3a8a' }}>{company.planDiscount || 0}%</span>
                                        </div>
                                        {company.couponCode && (
                                            <div className="detail-item">
                                                <span className="detail-label">Coupon code <span style={{ fontSize: '10px', verticalAlign: 'middle', color: '#16a34a' }}>(Applied)</span></span>
                                                <span className="detail-value" style={{ color: '#1e3a8a', fontWeight: 'bold' }}>{company.couponCode}</span>
                                            </div>
                                        )}
                                        {company.couponDiscountAmount > 0 && (
                                            <div className="detail-item">
                                                <span className="detail-label">Coupon Discount</span>
                                                <span className="detail-value" style={{ color: '#16a34a' }}>- ₹{company.couponDiscountAmount.toFixed(2)}</span>
                                            </div>
                                        )}
                                        <div className="detail-item" style={{ borderTop: '1px solid #e5e7eb', marginTop: '4px', paddingTop: '8px' }}>
                                            <span className="detail-label" style={{ fontWeight: '700' }}>Paid Amount</span>
                                            <span className="detail-value" style={{ color: '#059669', fontSize: '18px', fontWeight: '700' }}>
                                                ₹{company.finalPrice || 0}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="details-list-card">
                                        <div className="detail-item">
                                            <span className="detail-label">Start Date</span>
                                            <span className="detail-value">{formatDate(company.planStartDate)}</span>
                                        </div>
                                        <div className="detail-item">
                                            <span className="detail-label">Expiry Date</span>
                                            <span className="detail-value">{formatDate(company.planExpiryDate)}</span>
                                        </div>
                                        <div className="detail-item">
                                            <span className="detail-label">Status</span>
                                            <span className="detail-value">
                                                {(() => {
                                                    if (!company.planExpiryDate) return <span className="status-expired">Inactive</span>;
                                                    const now = new Date();
                                                    const expiry = new Date(company.planExpiryDate);
                                                    
                                                    // Normalize to start of day for calendar day difference
                                                    const diffDays = Math.floor((new Date(expiry).setHours(0,0,0,0) - new Date(now).setHours(0,0,0,0)) / (1000 * 60 * 60 * 24));
                                                    const isExpired = diffDays < 0;

                                                    if (isExpired) return <span className="status-expired">Expired</span>;
                                                    if (diffDays <= 10) return <span className="status-warning">{diffDays === 0 ? "Expiring Today" : `Expiring Soon (${diffDays}d)`}</span>;
                                                    return <span className="status-active">Active ({diffDays === 0 ? "Last day" : `${diffDays} days left`})</span>;
                                                })()}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {canViewCompany && (
                                    <div className="company-info-section">
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', borderBottom: '2px solid #f1f5f9', paddingBottom: '10px' }}>
                                            <h4 className="section-title" style={{ margin: 0, border: 'none', padding: 0 }}>Company Information</h4>
                                        </div>
                                        <div className="info-grid">
                                            <div className="info-item">
                                                <span className="info-label">Contact Person</span>
                                                <span className="info-value">{company.companyPersonName}</span>
                                            </div>
                                            <div className="info-item">
                                                <span className="info-label">GST Number</span>
                                                <span className="info-value">{company.companyGstNumber || '—'}</span>
                                            </div>
                                            <div className="info-item">
                                                <span className="info-label">PAN Number</span>
                                                <span className="info-value">{company.companyPanCardNumber || '—'}</span>
                                            </div>
                                            <div className="info-item full-width">
                                                <span className="info-label">Address</span>
                                                <span className="info-value">{company.companyAddress}</span>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <div className="company-info-section" style={{ marginTop: '24px' }}>
                                    <h4 className="section-title">Payment History</h4>
                                    {paymentHistoryLoading ? (
                                        <PageSkeleton />
                                    ) : paymentHistory.length > 0 ? (
                                        <div className="table-wrapper" style={{ marginTop: '16px' }}>
                                            <table className="panel-table">
                                                <thead>
                                                    <tr>
                                                        <th>Date</th>
                                                        <th>Amount Paid</th>
                                                        <th>Mode & Ref</th>
                                                        <th>Status</th>
                                                        <th>Admin Note / Reason</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {paymentHistory.map(payment => (
                                                        <tr key={payment.id || payment._id}>
                                                            <td>{formatDate(payment.createdAt)}</td>
                                                            <td style={{ fontWeight: '600', color: '#059669' }}>₹{payment.amountPaid}</td>
                                                            <td>
                                                                <div>{payment.paymentMode}</div>
                                                                {payment.transactionId && <small style={{ color: '#64748b' }}>{payment.transactionId}</small>}
                                                            </td>
                                                            <td>
                                                                <span className={`badge ${payment.paymentStatus === 'Verified' ? 'bg-success' : payment.paymentStatus === 'Rejected' ? 'bg-danger' : 'bg-warning text-dark'}`}>
                                                                    {payment.paymentStatus}
                                                                </span>
                                                            </td>
                                                            <td>
                                                                {payment.paymentStatus === 'Rejected' && payment.rejectionReason ? (
                                                                    <span style={{ color: '#ef4444', fontSize: '12px' }}>{payment.rejectionReason}</span>
                                                                ) : payment.paymentStatus === 'Verified' ? (
                                                                    <span style={{ color: '#10b981', fontSize: '12px' }}>Verified by {payment.verifiedByUserName || 'Admin'}</span>
                                                                ) : (
                                                                    <span style={{ color: '#64748b', fontSize: '12px' }}>Awaiting verification</span>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    ) : (
                                        <div className="panel-empty" style={{ marginTop: '16px' }}>No payment history found.</div>
                                    )}
                                </div>
                            </div>
                        </div>
                    ) : canViewCompany && activeTab === 'dashboard' ? (
                        <div className="profile-panel dashboard-panel" style={{ padding: '24px' }}>
                            {dashboardLoading ? (
                                <PageSkeleton />
                            ) : activeSubView !== 'dashboard' ? (
                                <div className="inline-subview-container">
                                    <div className="inline-subview-header" style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <button 
                                            className="profile-btn-back" 
                                            style={{ 
                                                display: 'flex', 
                                                alignItems: 'center', 
                                                gap: '8px', 
                                                padding: '8px 16px', 
                                                background: '#0b3a54', 
                                                color: '#fff', 
                                                border: 'none', 
                                                borderRadius: '6px', 
                                                cursor: 'pointer', 
                                                fontWeight: '600',
                                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                                transition: 'all 0.2s ease'
                                            }}
                                            onClick={() => setActiveSubView('dashboard')}
                                        >
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <line x1="19" y1="12" x2="5" y2="12"></line>
                                                <polyline points="12 19 5 12 12 5"></polyline>
                                            </svg>
                                            Back to Dashboard
                                        </button>
                                    </div>

                                    <div className="inline-subview-body">
                                        {activeSubView === 'Purchase Invoice' && (
                                            <PurchaseInvoiceList 
                                                isInline={true} 
                                                companyId={company._id || company.id}
                                                onPreview={onPreviewPurchaseInvoice}
                                                onAddInvoice={onAddPurchaseInvoice}
                                                onEditInvoice={onEditPurchaseInvoice}
                                                onNavigateToProfile={handleInlineNavigateToSeller}
                                            />
                                        )}
                                        {activeSubView === 'Sales Invoice' && (
                                            <InvoiceList 
                                                isInline={true} 
                                                companyId={company._id || company.id}
                                                onPreview={onPreviewInvoice}
                                                onAddInvoice={onAddInvoice}
                                                onEditInvoice={onEditInvoice}
                                                onNavigateToProfile={handleInlineNavigateToCustomer}
                                            />
                                        )}
                                                                                                                        {activeSubView === 'Seller' && (
                                            inlineProfileType === 'Seller' ? (
                                                <SellerProfile 
                                                    seller={inlineProfileData} 
                                                    companyId={company._id || company.id}
                                                    onPreview={onPreviewPurchaseInvoice}
                                                    onBack={() => { 
                                                        setInlineProfileType(null); 
                                                        setInlineProfileData(null); 
                                                        if (previousSubView) {
                                                            setActiveSubView(previousSubView);
                                                            setPreviousSubView(null);
                                                        }
                                                    }}
                                                />
                                            ) : (
                                                <SellerList 
                                                    isInline={true} 
                                                    companyId={company._id || company.id}
                                                    onNavigateToProfile={handleInlineNavigateToSeller}
                                                />
                                            )
                                        )}
                                                                                                                        {activeSubView === 'Customer' && (
                                            inlineProfileType === 'Customer' ? (
                                                <CustomerProfile 
                                                    customer={inlineProfileData} 
                                                    companyId={company._id || company.id}
                                                    onPreviewInvoice={onPreviewInvoice}
                                                    onBack={() => { 
                                                        setInlineProfileType(null); 
                                                        setInlineProfileData(null); 
                                                        if (previousSubView) {
                                                            setActiveSubView(previousSubView);
                                                            setPreviousSubView(null);
                                                        }
                                                    }}
                                                />
                                            ) : (
                                                <CustomerList 
                                                    isInline={true} 
                                                    companyId={company._id || company.id}
                                                    onNavigateToProfile={handleInlineNavigateToCustomer}
                                                />
                                            )
                                        )}
                                                                                {activeSubView === 'Transporter' && (
                                            inlineProfileType === 'Transporter' ? (
                                                <TransporterProfile 
                                                    transporter={inlineProfileData} 
                                                    onBack={() => { 
                                                        setInlineProfileType(null); 
                                                        setInlineProfileData(null); 
                                                        if (previousSubView) {
                                                            setActiveSubView(previousSubView);
                                                            setPreviousSubView(null);
                                                        }
                                                    }}
                                                />
                                            ) : (
                                                <TransporterList 
                                                    isInline={true} 
                                                    companyId={company._id || company.id}
                                                    onNavigateToProfile={handleInlineNavigateToTransporter}
                                                />
                                            )
                                        )}
                                        {activeSubView === 'Product' && (
                                            <ProductList isInline={true} companyId={company._id || company.id} />
                                        )}
                                        {activeSubView === 'Category' && (
                                            <CategoryList isInline={true} companyId={company._id || company.id} />
                                        )}
                                        {activeSubView === 'InStock' && (
                                            <InStock isInline={true} companyId={company._id || company.id} />
                                        )}
                                        {activeSubView === 'OutStock' && (
                                            <OutStock isInline={true} companyId={company._id || company.id} />
                                        )}
                                    </div>
                                </div>
                            ) : (
                                <div className="company-dashboard-overview">
                                    <div className="dashboard-grid">
                                        {cards.map((card) => (
                                            <button
                                                key={card.title}
                                                type="button"
                                                className="dashboard-card"
                                                style={{ '--card-color': card.color }}
                                                onClick={() => card.module && setActiveSubView(card.module)}
                                                disabled={!card.module}
                                            >
                                                <div className="dashboard-card__top">
                                                    <span className="dashboard-card__count">{card.count}</span>
                                                    <div className="dashboard-card__icon">{card.icon}</div>
                                                </div>
                                                <div className="dashboard-card__bottom">
                                                    <h3 className="dashboard-card__title">{card.title}</h3>
                                                </div>
                                            </button>
                                        ))}
                                    </div>

                                    <DashboardTables
                                        purchaseData={mappedRecentPurchasePayments}
                                        salesData={mappedRecentSalesPayments}
                                        forceShow={isPrivileged || canShowAll}
                                    />

                                    <div className="dashboard-tables mt-32" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '32px', marginTop: '32px' }}>
                                        <LowStockAlertTable products={mappedLowStock} forceShow={isPrivileged || canShowAll} />
                                        <PieChartComponent data={dashboardStats.pieData || []} />
                                    </div>

                                    <DashboardCharts
                                        salesData={dashboardStats.salesHistory || []}
                                        purchaseVsPaymentData={dashboardStats.purchaseVsPayment || []}
                                        forceShow={isPrivileged || canShowAll}
                                    />
                                </div>
                            )}
                        </div>
                    ) : null}
                </div>
            </div>
            
            {(() => {
                const hasVerifiedPayment = paymentHistory.some(p => p.paymentStatus === 'Verified');
                const effectiveCouponCode = hasVerifiedPayment ? null : company.couponCode;
                const effectiveCouponDiscount = hasVerifiedPayment ? 0 : company.couponDiscountAmount;
                
                let effectiveFinalPrice = company.finalPrice;
                if (hasVerifiedPayment && company.couponDiscountAmount > 0) {
                    effectiveFinalPrice = company.planPrice - (company.planPrice * (company.planDiscount || 0) / 100);
                }

                return (
                    <PlanPaymentModal
                        isOpen={isPaymentModalOpen}
                        onClose={() => setIsPaymentModalOpen(false)}
                        companyId={company._id || company.id}
                        planId={company.planId}
                        planName={company.planName}
                        finalPrice={effectiveFinalPrice}
                        planPrice={company.planPrice}
                        planDiscount={company.planDiscount}
                        defaultCouponCode={effectiveCouponCode}
                        defaultCouponDiscount={effectiveCouponDiscount}
                        onSuccess={() => {
                            fetchPaymentHistory();
                        }}
                    />
                );
            })()}

            </div>
        </PermissionContext.Provider>
    );
};

export default CompanyProfile;
